import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Booking, OwnerEvent, clubsBookingApi } from '@/api/clubs-booking';
import {
  AdminDataTable,
  AdminSection,
  adminInputClassName,
  adminPrimaryButtonClassName,
  adminTableCellClassName,
  adminTableClassName,
  adminTableHeadCellClassName,
  adminToolbarActionsClassName,
  adminToolbarClassName,
} from '@/components/admin/AdminLayout';
import AdminMobileRowActions from '@/components/admin/AdminMobileRowActions';
import AdminShell from '@/components/admin/AdminShell';
import TablePagination from '@/components/common/TablePagination';
import { auth } from '@/store/authStore';
import { getDisplayContactEmail } from '@/utils/contactDetails';
import { formatCurrencyFromCents } from '@/utils/currency';
import { useStripeConnectStatus } from '@/contexts/StripeConnectStatusContext';

const getBookingReference = (booking: Booking) =>
  booking.paymentReference?.trim() || `BK-${booking._id.slice(-8).toUpperCase()}`;

const getBookingChildrenLabel = (booking: Booking) => {
  const names = Array.isArray(booking.children)
    ? booking.children
        .map((child) => [String(child.firstName || '').trim(), String(child.lastName || '').trim()].filter(Boolean).join(' ').trim())
        .filter(Boolean)
    : [];
  return names.length > 0 ? names.join(', ') : 'Child';
};

const BookingsPage = () => {
  const navigate = useNavigate();
  const currentUser = auth.getUser();
  const isOrganizationOwner = currentUser.isOrganizationOwner;
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [events, setEvents] = useState<OwnerEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState(searchParams.get('eventId') || '');
  const highlightedBookingId = searchParams.get('bookingId') || '';
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);
  const [loading, setLoading] = useState(true);
  const [bookingsTotal, setBookingsTotal] = useState(0);
  const [stripeBusyAction, setStripeBusyAction] = useState<'onboarding' | 'dashboard' | null>(null);
  const [stripeMessage, setStripeMessage] = useState('');
  const [stripeError, setStripeError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deletingBookingId, setDeletingBookingId] = useState('');
  const {
    status: stripeStatus,
    loading: stripeStatusLoading,
    isStripeReady,
    refreshStatus: refreshStripeStatus,
  } = useStripeConnectStatus();

  const loadBookings = async (eventId?: string) => {
    setLoading(true);
    try {
      const bookingData = await clubsBookingApi.listMyBookings(eventId, {
        page,
        limit: pageSize,
      });
      setBookings(bookingData.items || []);
      setBookingsTotal(Number(bookingData.total || 0));
    } finally {
      setLoading(false);
    }
  };

  const loadEventOptions = async () => {
    try {
      const eventData = await clubsBookingApi.listMyEvents(undefined, {
        page: 1,
        limit: 500,
      });
      setEvents(eventData.items || []);
    } catch {
      setEvents([]);
    }
  };

  useEffect(() => {
    void loadBookings(selectedEventId || undefined);
  }, [page, pageSize, selectedEventId]);

  useEffect(() => {
    void loadEventOptions();
  }, []);

  useEffect(() => {
    const stripeQueryState = searchParams.get('stripe');
    if (stripeQueryState !== 'return' && stripeQueryState !== 'refresh') return;

    const refreshStatus = async () => {
      try {
        await refreshStripeStatus(true);
        if (stripeQueryState === 'return') {
          setStripeMessage('');
          setStripeError('');
        } else {
          setStripeError('Stripe onboarding was interrupted. Continue setup to accept card payments.');
          setStripeMessage('');
        }
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setStripeError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not refresh Stripe status');
      }
    };

    void refreshStatus();
  }, [refreshStripeStatus, searchParams]);

  useEffect(() => {
    setPage(1);
  }, [selectedEventId]);

  const formatPrice = (amountCents: number, currencyCode: string) =>
    formatCurrencyFromCents(amountCents, currencyCode || 'GBP');

  const markPaid = async (booking: Booking, paid: boolean) => {
    if (booking.paymentMethod !== 'cash') {
      return;
    }
    await clubsBookingApi.updateBookingPayment(booking._id, {
      paymentStatus: paid ? 'paid' : 'pending',
      paymentReference: booking.paymentReference,
    });
    await loadBookings(selectedEventId || undefined);
  };

  const deleteBooking = async (booking: Booking) => {
    const reference = getBookingReference(booking);
    const confirmed = window.confirm(
      `Delete booking ${reference}?\n\nThis will permanently delete the booking and any linked attendee records. This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingBookingId(booking._id);
    setDeleteError('');
    try {
      await clubsBookingApi.deleteMyBooking(booking._id);
      await loadBookings(selectedEventId || undefined);
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setDeleteError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not delete booking');
    } finally {
      setDeletingBookingId('');
    }
  };

  const startStripeOnboarding = async () => {
    setStripeBusyAction('onboarding');
    setStripeError('');
    try {
      const { url } = await clubsBookingApi.createStripeConnectOnboardingLink({
        returnUrl: `${window.location.origin}/bookings?stripe=return`,
        refreshUrl: `${window.location.origin}/bookings?stripe=refresh`,
      });
      window.location.assign(url);
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setStripeError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not start Stripe setup');
      setStripeBusyAction(null);
    }
  };

  const openStripeConnectDashboard = async () => {
    setStripeBusyAction('dashboard');
    setStripeError('');
    try {
      const { url } = await clubsBookingApi.createStripeConnectDashboardLink();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setStripeError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not open Stripe Connect dashboard');
    } finally {
      setStripeBusyAction(null);
    }
  };

  return (
    <AdminShell title="Bookings" subtitle="Track payment statuses and booking values">
      <AdminSection>
        {(stripeError || stripeMessage || deleteError) && (
          <div
            className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
              stripeError || deleteError
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
          >
            {deleteError || stripeError || stripeMessage}
          </div>
        )}

        <div className={adminToolbarClassName}>
          <h2 className="text-2xl font-semibold">Bookings and payments</h2>
          <div className={adminToolbarActionsClassName}>
            <select
              className={adminInputClassName}
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              <option value="">All activities</option>
              {events.map((event) => (
                <option key={event._id} value={event._id}>
                  {event.title}
                </option>
              ))}
            </select>
            <Link to="/bookings/new" className={adminPrimaryButtonClassName}>
              Create booking
            </Link>
          </div>
        </div>

        <AdminDataTable data-testid="bookings-table-shell">
          <table className={`${adminTableClassName} min-w-[840px] sm:min-w-[960px]`}>
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className={adminTableHeadCellClassName}>Reference</th>
                <th className={adminTableHeadCellClassName}>Parent</th>
                <th className={adminTableHeadCellClassName}>Child</th>
                <th className={adminTableHeadCellClassName}>Qty</th>
                <th className={adminTableHeadCellClassName}>Amount</th>
                <th className={adminTableHeadCellClassName}>Discount</th>
                <th className={adminTableHeadCellClassName}>Method</th>
                <th className={adminTableHeadCellClassName}>Payment</th>
                <th className={`${adminTableHeadCellClassName} hidden sm:table-cell`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td className="px-3 py-6 text-sm text-slate-500 sm:px-4" colSpan={9}>
                    Loading bookings...
                  </td>
                </tr>
              )}
              {bookings.map((booking) => (
                <tr
                  key={booking._id}
                  className={`${booking._id === highlightedBookingId ? 'bg-[#ECF8F3]' : ''} cursor-pointer hover:bg-slate-50`}
                  onClick={() => navigate(`/bookings/${booking._id}`)}
                >
                  <td className={`${adminTableCellClassName} font-medium text-[#1F8F5F]`}>
                    <div className="flex items-start justify-between gap-3">
                      <span>{getBookingReference(booking)}</span>
                      <AdminMobileRowActions
                        title={getBookingReference(booking)}
                        description="Manage this booking."
                        items={[
                          {
                            label: 'View booking',
                            onSelect: () => {
                              navigate(`/bookings/${booking._id}`);
                            },
                          },
                          ...(booking.paymentMethod === 'cash'
                            ? [
                                {
                                  label: `Mark ${booking.paymentStatus === 'paid' ? 'pending' : 'paid'}`,
                                  onSelect: () => {
                                    void markPaid(booking, booking.paymentStatus !== 'paid');
                                  },
                                },
                              ]
                            : []),
                          {
                            label: deletingBookingId === booking._id ? 'Deleting...' : 'Delete booking',
                            tone: 'danger',
                            onSelect: () => {
                              void deleteBooking(booking);
                            },
                          },
                        ]}
                      />
                    </div>
                  </td>
                  <td className={adminTableCellClassName}>
                    <div className="font-medium">{booking.parentName}</div>
                    <div className="text-xs text-slate-500">{getDisplayContactEmail(booking.parentEmail)}</div>
                  </td>
                  <td className={adminTableCellClassName}>{getBookingChildrenLabel(booking)}</td>
                  <td className={adminTableCellClassName}>{booking.quantity}</td>
                  <td className={adminTableCellClassName}>
                    {formatPrice(booking.totalPriceCents, booking.currency)}
                    <div className="text-xs text-slate-500">
                      Subtotal {formatPrice(booking.preDiscountTotalCents ?? booking.totalPriceCents, booking.currency)}
                    </div>
                  </td>
                  <td className={adminTableCellClassName}>
                    -{formatPrice(booking.discountTotalCents || 0, booking.currency)}
                    {!!booking.appliedDiscounts?.length && (
                      <div className="mt-1 text-xs text-slate-500">
                        {booking.appliedDiscounts.map((discount) => discount.summary).join(', ')}
                      </div>
                    )}
                  </td>
                  <td className={`${adminTableCellClassName} capitalize`}>{booking.paymentMethod.replace('_', ' ')}</td>
                  <td className={adminTableCellClassName}>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${
                        booking.paymentStatus === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {booking.paymentStatus}
                    </span>
                  </td>
                  <td className={`${adminTableCellClassName} hidden sm:table-cell`}>
                    <div className="flex flex-wrap gap-2">
                      {booking.paymentMethod === 'cash' && (
                        <button
                          className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            void markPaid(booking, booking.paymentStatus !== 'paid');
                          }}
                        >
                          Mark {booking.paymentStatus === 'paid' ? 'pending' : 'paid'}
                        </button>
                      )}
                      <button
                        className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/bookings/${booking._id}`);
                        }}
                      >
                        View
                      </button>
                      <button
                        className="rounded-md border border-red-300 bg-red-50 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={deletingBookingId === booking._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          void deleteBooking(booking);
                        }}
                      >
                        {deletingBookingId === booking._id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && bookingsTotal === 0 && <p className="px-4 py-6 text-sm text-slate-500">No bookings found.</p>}
          {!loading && bookingsTotal > 0 && (
            <TablePagination
              totalItems={bookingsTotal}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          )}
        </AdminDataTable>

        {isOrganizationOwner && stripeStatusLoading && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="bookings-stripe-status-loading">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <Loader2 className="h-4 w-4 animate-spin text-amber-700" />
              Checking online payments setup...
            </div>
          </div>
        )}

        {isOrganizationOwner && !stripeStatusLoading && (
          <div
            className={`mt-6 rounded-xl border p-4 ${
              isStripeReady ? 'border-emerald-200 bg-emerald-50/60' : 'border-amber-200 bg-amber-50'
            }`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-800">Parent Payment Status</p>
                <p className="mt-1 text-xs text-slate-600">
                  {isStripeReady
                    ? 'Session Monkey partners with Stripe to power online payments. Money collected from parents online will go into this account.'
                    : 'Parents can pay by card once Stripe onboarding is complete.'}
                </p>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  isStripeReady
                    ? 'bg-emerald-100 text-emerald-700'
                    : stripeStatus?.hasAccount
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-200 text-slate-700'
                }`}
              >
                {isStripeReady ? 'Ready' : stripeStatus?.hasAccount ? 'Setup required' : 'Not connected'}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {isStripeReady ? (
                <button
                  type="button"
                  className={`${adminPrimaryButtonClassName} px-3 disabled:opacity-60`}
                  onClick={() => void openStripeConnectDashboard()}
                  disabled={stripeBusyAction === 'dashboard'}
                >
                  {stripeBusyAction === 'dashboard' ? 'Opening...' : 'Manage payments account'}
                </button>
              ) : (
                <button
                  type="button"
                  className={`${adminPrimaryButtonClassName} px-3 disabled:opacity-60`}
                  onClick={() => void startStripeOnboarding()}
                  disabled={stripeBusyAction === 'onboarding'}
                >
                  {stripeBusyAction === 'onboarding'
                    ? 'Opening...'
                    : stripeStatus?.hasAccount
                    ? 'Continue Stripe setup'
                    : 'Connect Stripe'}
                </button>
              )}
            </div>
          </div>
        )}
      </AdminSection>
    </AdminShell>
  );
};

export default BookingsPage;
